import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings2, Loader2, Save, Database, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getSettings, updateSettings, listSchemaVersions, listStandards, setLgaSchema } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SettingsPage() {
  const [versions, setVersions] = useState([]);
  const [schemaVersion, setSchemaVersion] = useState("");
  const [datum, setDatum] = useState("");
  const [pitSnap, setPitSnap] = useState("");
  const [busy, setBusy] = useState(false);
  const [standards, setStandards] = useState([]);
  const [lga, setLga] = useState("");
  const [lgaBusy, setLgaBusy] = useState(false);
  const { user, isPro, refresh } = useAuth();

  useEffect(() => {
    listSchemaVersions().then(setVersions);
    listStandards().then(setStandards).catch(() => {});
    getSettings().then((s) => { setSchemaVersion(s.schema_version || ""); setDatum(s.default_datum || ""); setPitSnap(s.pit_snap_m ?? 2.0); });
  }, []);

  useEffect(() => { setLga(user?.lga_schema || ""); }, [user?.lga_schema]);

  const saveLga = async () => {
    if (!lga) return;
    setLgaBusy(true);
    try {
      await setLgaSchema(lga);
      await refresh();
      toast.success("LGA schema saved — your free QA is locked to this authority");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save LGA schema");
    } finally {
      setLgaBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await updateSettings({ schema_version: schemaVersion, default_datum: datum, pit_snap_m: parseFloat(pitSnap) || 2.0 });
      toast.success("Software setup saved — applies to validation & new projects");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setBusy(false);
    }
  };

  const active = versions.find((v) => v.id === schemaVersion);

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-10">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Setup</h1>
          <p className="font-mono text-[11px] text-zinc-500">Software-wide configuration held for future projects</p>
        </div>
      </header>

      <div className="p-6 max-w-2xl space-y-5">
        {!isPro && (
          <div className="border border-zinc-800 rounded-sm bg-[#101010] p-5 space-y-4" data-testid="lga-setup">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
              <MapPin className="w-3.5 h-3.5" /> Your LGA / authority schema
            </div>
            <p className="font-mono text-[11px] text-zinc-400">On the free plan, QA tolerance assessment is locked to a single local authority schema. Pick yours below — subscribe to use all authorities.</p>
            <div className="space-y-1.5">
              <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Local authority schema</label>
              <Select value={lga} onValueChange={setLga}>
                <SelectTrigger data-testid="lga-select" className="h-10 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs"><SelectValue placeholder="Select your authority…" /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-72">
                  {standards.map((s) => (
                    <SelectItem key={s.id} value={s.id} className="font-mono text-xs">{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={saveLga} disabled={lgaBusy || !lga} data-testid="save-lga-btn" className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white">
              {lgaBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save LGA schema
            </Button>
          </div>
        )}

        <div className="border border-zinc-800 rounded-sm bg-[#101010] p-5 space-y-4" data-testid="schema-setup">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
            <Settings2 className="w-3.5 h-3.5" /> ADAC schema version
          </div>
          <p className="font-mono text-[11px] text-zinc-400">Different assessing bodies mandate different ADAC schema versions. Set the version your authority requires — it's used for validation and inherited by every new project.</p>

          <div className="space-y-1.5">
            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Active schema version</label>
            <Select value={schemaVersion} onValueChange={setSchemaVersion}>
              <SelectTrigger data-testid="schema-version-select" className="h-10 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs"><SelectValue placeholder="Select version…" /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
                {versions.map((v) => (
                  <SelectItem key={v.id} value={v.id} className="font-mono text-xs">{v.label} · {v.status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {active && (
            <div className="border border-zinc-800 rounded-sm bg-[#0C0C0C] p-3 space-y-1" data-testid="schema-version-info">
              <div className="font-mono text-[11px] text-zinc-300">{active.label} <span className="text-zinc-600">· datum {active.datum}</span></div>
              <p className="font-mono text-[10px] text-zinc-500">{active.notes}</p>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Default datum / projection</label>
            <Input value={datum} onChange={(e) => setDatum(e.target.value)} data-testid="datum-input" placeholder="GDA2020 / MGA56" className="h-10 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs" />
          </div>

          <div className="space-y-1.5">
            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Pipe-string pit snap tolerance (m)</label>
            <Input type="number" step="0.1" min="0.1" value={pitSnap} onChange={(e) => setPitSnap(e.target.value)} data-testid="pit-snap-input" placeholder="2.0" className="h-10 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs" />
            <p className="font-mono text-[10px] text-zinc-500">Long-section string detection snaps each pipe end to the nearest pit within this distance to decide connectivity. Increase for surveys with more coordinate drift; decrease to be stricter.</p>
          </div>

          <Button onClick={save} disabled={busy} data-testid="save-settings-btn" className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save setup
          </Button>
        </div>

        <div className="border border-zinc-800 rounded-sm bg-[#101010] p-5">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3"><Database className="w-3.5 h-3.5" /> Supported versions</div>
          <div className="space-y-2">
            {versions.map((v) => (
              <div key={v.id} className="flex items-start gap-3 font-mono text-[11px]">
                <span className={`px-2 py-0.5 rounded-sm shrink-0 ${v.id === schemaVersion ? "bg-blue-600/15 border border-blue-600/30 text-blue-400" : "bg-zinc-900 border border-zinc-800 text-zinc-400"}`}>{v.label}</span>
                <span className="text-zinc-500">{v.notes}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
